// src/components/SignalsPage.jsx
// Global Strategic Signals page — all signals across all accounts
// Filters: Signal Type · Signal Strength · Freshness · Account · Verified/Derived

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './SignalsPage.css';
// P0.31 31.B.6 — one archive confirm for every entity, so the copy cannot drift.
import { confirmArchive } from '../lib/archiveConfirm';

const TYPE_CONFIG = {
  Exec:                 { color: 'var(--dm-indigo)', bg: '#EEF2FF', icon: '👤', label: 'Executive' },
  Executive:            { color: 'var(--dm-indigo)', bg: '#EEF2FF', icon: '👤', label: 'Executive' },
  'Leadership Change':  { color: 'var(--dm-purple-vivid)', bg: 'var(--dm-purple-pale)', icon: '🔄', label: 'Leadership' },
  Financial:            { color: 'var(--dm-red)', bg: 'var(--dm-red-light)', icon: '💰', label: 'Financial' },
  AI:                   { color: 'var(--dm-purple-vivid)', bg: 'var(--dm-purple-pale)', icon: '🤖', label: 'AI' },
  'AI/Tech':            { color: 'var(--dm-purple-vivid)', bg: 'var(--dm-purple-pale)', icon: '🤖', label: 'AI / Tech' },
  Data:                 { color: 'var(--dm-cyan)', bg: '#E0F2FE', icon: '📊', label: 'Data' },
  'Technology Initiative': { color: 'var(--dm-cyan)', bg: '#E0F2FE', icon: '⚙️', label: 'Tech Initiative' },
  Market:               { color: 'var(--dm-amber)', bg: 'var(--dm-amber-light)', icon: '📈', label: 'Market' },
  Regulatory:           { color: 'var(--dm-amber-deep)', bg: 'var(--dm-amber-light)', icon: '⚖️', label: 'Regulatory' },
  'Strategic Inference':{ color: 'var(--dm-blue-deep)', bg: 'var(--dm-blue-light)', icon: '🧠', label: 'Derived' },
};

const STRENGTH_CONFIG = {
  Strong:   { color: 'var(--dm-green)', bg: 'var(--dm-green-light)', icon: '🔥', label: 'Strong' },
  Moderate: { color: 'var(--dm-amber)', bg: 'var(--dm-amber-light)', icon: '⚡', label: 'Moderate' },
  Weak:     { color: 'var(--dm-grey-600)', bg: '#F3F4F6', icon: '💧', label: 'Weak' },
};

// Derived confidence config — blue family to distinguish from verified
const CONFIDENCE_CONFIG = {
  Logical:     { color: 'var(--dm-blue-deep)', bg: 'var(--dm-blue-light)', icon: '🧠', label: 'Logical' },
  Speculative: { color: 'var(--dm-indigo-deep)', bg: '#EEF2FF', icon: '💭', label: 'Speculative' },
  Hypothesis:  { color: 'var(--dm-indigo-soft)', bg: '#F0F0FF', icon: '🔮', label: 'Hypothesis' },
};

const FRESHNESS_CONFIG = {
  fresh:      { color: 'var(--dm-green)', bg: 'var(--dm-green-light)', label: 'Fresh',      days: '≤ 30d' },
  recent:     { color: 'var(--dm-cyan)', bg: '#E0F2FE', label: 'Recent',     days: '31–60d' },
  aging:      { color: 'var(--dm-amber)', bg: 'var(--dm-amber-light)', label: 'Aging',      days: '61–90d' },
  stale:      { color: 'var(--dm-grey-500)', bg: '#F3F4F6', label: 'Stale',      days: '91–180d' },
  'very-stale':{ color: '#EF4444', bg: 'var(--dm-red-light)', label: 'Very Stale', days: '180d+' },
  unknown:    { color: 'var(--dm-grey-500)', bg: '#F3F4F6', label: 'No Date',    days: '—' },
};

// Composite score for sorting — relevance + recency + confidence
function signalScore(sig) {
  let score = 0;
  // Strength/confidence
  if (sig.isDerived) {
    if (sig.derivedConfidence === 'Logical')     score += 60;
    else if (sig.derivedConfidence === 'Speculative') score += 40;
    else score += 20;
  } else {
    if (sig.signalStrength === 'Strong')   score += 100;
    else if (sig.signalStrength === 'Moderate') score += 60;
    else score += 30;
  }
  // Recency
  const days = sig.ageInDays;
  if (days == null)    score += 0;
  else if (days <= 7)  score += 40;
  else if (days <= 30) score += 30;
  else if (days <= 60) score += 20;
  else if (days <= 90) score += 10;
  return score;
}

function FreshnessTag({ freshness }) {
  const cfg = FRESHNESS_CONFIG[freshness] || FRESHNESS_CONFIG.unknown;
  return (
    <span className="sp-freshness-tag" style={{ color: cfg.color, background: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

function SignalCard({ signal, accountName, onNavigate, onArchive, onRestore, busy }) {
  const [showReasoning, setShowReasoning] = useState(false);

  const isDerived = signal.isDerived;
  const conf      = isDerived ? CONFIDENCE_CONFIG[signal.derivedConfidence] || CONFIDENCE_CONFIG.Hypothesis : null;
  const type      = TYPE_CONFIG[isDerived ? 'Strategic Inference' : signal.signalType]
                    || { color: 'var(--dm-grey-600)', bg: '#F3F4F6', icon: '📋', label: signal.signalType || 'Signal' };
  const strength  = isDerived ? conf : (STRENGTH_CONFIG[signal.signalStrength] || { color: 'var(--dm-grey-600)', bg: '#F3F4F6', icon: '💧', label: signal.signalStrength });

  const displayDate = signal.signalDate || signal.dateObserved;
  const dateLabel = displayDate
    ? new Date((displayDate || '').slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const handleAccountClick = (e) => {
    e.stopPropagation();
    if (signal.accountId && onNavigate) onNavigate('account-detail', signal.accountId);
  };

  const handleFloorClick = (e) => {
    e.stopPropagation();
    if (signal.accountId && onNavigate) {
      onNavigate('floor', null, {
        accountId: signal.accountId, accountName,
        signal: signal.name, signalType: signal.signalType,
        signalStrength: signal.signalStrength,
        aiInterpretation: signal.aiInterpretation,
      });
    }
  };

  return (
    <div className={`sp-card ${isDerived ? 'sp-card--derived' : 'sp-card--verified'}`}>
      {/* Derived indicator strip */}
      {isDerived && <div className="sp-derived-strip" />}

      {/* Header row — badges + date */}
      <div className="sp-card-header">
        {isDerived ? (
          <>
            <span className="sp-derived-badge" style={{ color: conf?.color, background: conf?.bg }}>
              {conf?.icon} Derived · {signal.derivedConfidence}
            </span>
            <span className="sp-type-badge" style={{ color: type.color, background: type.bg }}>
              {type.icon} {type.label}
            </span>
          </>
        ) : (
          <>
            <span className="sp-type-badge" style={{ color: type.color, background: type.bg }}>
              <span className="sp-type-icon">{type.icon}</span>
              {type.label}
            </span>
            {signal.signalStrength && (
              <span className="sp-strength-badge" style={{ color: strength.color, background: strength.bg }}>
                {strength.icon} {signal.signalStrength}
              </span>
            )}
          </>
        )}
        <FreshnessTag freshness={signal.freshness} />
        {dateLabel && <span className="sp-date">{dateLabel}</span>}
      </div>

      {/* Account name */}
      {accountName ? (
        <div className="sp-account-name" onClick={handleAccountClick} title="Open account">
          {accountName}
        </div>
      ) : (
        <div className="sp-account-name sp-account-name--unknown">Unknown Account</div>
      )}

      {/* Signal name */}
      <div className="sp-signal-name">{signal.name}</div>

      {/* AI interpretation */}
      {signal.aiInterpretation && (
        <p className="sp-interpretation">💡 {signal.aiInterpretation}</p>
      )}

      {/* Derived reasoning — expandable */}
      {isDerived && signal.derivedReasoning && (
        <div className="sp-derived-reasoning">
          <button
            className="sp-reasoning-toggle"
            onClick={(e) => { e.stopPropagation(); setShowReasoning(r => !r); }}
          >
            🔗 {signal.sourceSignalIds?.length || 0} source signal{(signal.sourceSignalIds?.length || 0) !== 1 ? 's' : ''} · {showReasoning ? 'Hide reasoning ↑' : 'Show reasoning ↓'}
          </button>
          {showReasoning && (
            <div className="sp-reasoning-body">
              <div className="sp-reasoning-label">Jarvis reasoning chain:</div>
              <div className="sp-reasoning-text">{signal.derivedReasoning}</div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="sp-card-footer">
        <div className="sp-footer-left">
          {signal.source && <span className="sp-source">📎 {signal.source}</span>}
          {signal.sourceLink && (
            <a href={signal.sourceLink} target="_blank" rel="noopener noreferrer"
              className="sp-source-link" onClick={e => e.stopPropagation()}>
              View source →
            </a>
          )}
        </div>
        {signal.accountId && (
          <button className="sp-floor-btn" onClick={handleFloorClick} title="Discuss in The Floor">
            ⚡ Floor
          </button>
        )}
        {/* P0.31 31.B.3 — THIS PAGE HAD NO REMOVAL AT ALL. api/signals/delete.js
            has existed as a soft delete the whole time and was reachable only
            from an account's Signals tab, so a user working from the left menu
            could not remove a signal from anywhere. */}
        {signal.deletedAt ? (
          <button className="sp-restore-btn" disabled={busy}
                  onClick={e => { e.stopPropagation(); onRestore && onRestore(signal); }}
                  title="Restore this signal">
            {busy ? '…' : '↩ Restore'}
          </button>
        ) : (
          <button className="sp-archive-btn" disabled={busy}
                  onClick={e => { e.stopPropagation(); onArchive && onArchive(signal); }}
                  title="Archive this signal">
            {busy ? '…' : 'Archive'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function SignalsPage({ onNavigate }) {
  const [signals, setSignals]       = useState([]);
  const [accounts, setAccounts]     = useState({});
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const [typeFilter,      setTypeFilter]      = useState('all');
  const [strengthFilter,  setStrengthFilter]  = useState('all');
  const [freshnessFilter, setFreshnessFilter] = useState('all');
  const [accountFilter,   setAccountFilter]   = useState('all');
  const [derivedFilter,   setDerivedFilter]   = useState('all'); // 'all' | 'verified' | 'derived'
  const [search,          setSearch]          = useState('');
  // P0.31 31.B.5 — the Archived view. Fetched from the server rather than
  // partitioned client-side, because an archived signal is EXCLUDED by the
  // default query; there is nothing to partition until it is asked for.
  const [showArchived,    setShowArchived]    = useState(false);
  const [acting,          setActing]          = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sigRes, acctRes] = await Promise.all([
        apiFetch('/api/signals/by-account?accountId=all&limit=200'
                 + (showArchived ? '&archived=1' : '')),
        apiFetch('/api/accounts/list'),
      ]);
      const sigData  = await sigRes.json();
      const acctData = await acctRes.json();
      if (sigData.success) setSignals(sigData.signals || []);
      else setError(sigData.error || 'Failed to load signals');
      const acctMap = {};
      const acctList = acctData.accounts || acctData || [];
      acctList.forEach(a => { if (a.id) acctMap[a.id.replace(/-/g, '')] = a.accountName; });
      setAccounts(acctMap);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // `showArchived` IS A DEPENDENCY. Left as [] the callback closes over the
  // first render's value, so toggling the view refetches the SAME list and the
  // user sees nothing change — a stale-closure bug that looks like a broken
  // toggle rather than a bug.
  }, [showArchived]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // P0.31 31.B.3/31.B.5 — archive and restore, from the page the user is on.
  const archiveSignal = async (signal) => {
    if (!confirmArchive({
      entity: 'signal',
      name: signal.name,
      keeps: 'Nothing is deleted, and the account keeps its other signals.',
    })) return;
    setActing(a => ({ ...a, [signal.id]: true }));
    try {
      const res = await apiFetch('/api/signals/delete', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: signal.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to archive');
      // Dropped from THIS list rather than refetched: the row no longer belongs
      // in the view the user is looking at, either way round.
      setSignals(prev => prev.filter(x => x.id !== signal.id));
    } catch (err) {
      alert('Could not archive that signal: ' + err.message);
    } finally {
      setActing(a => { const n = { ...a }; delete n[signal.id]; return n; });
    }
  };

  const restoreSignal = async (signal) => {
    setActing(a => ({ ...a, [signal.id]: true }));
    try {
      const res = await apiFetch('/api/signals/unarchive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: signal.id }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to restore');
      setSignals(prev => prev.filter(x => x.id !== signal.id));
    } catch (err) {
      alert('Could not restore that signal: ' + err.message);
    } finally {
      setActing(a => { const n = { ...a }; delete n[signal.id]; return n; });
    }
  };

  const getAccountName = (sig) => {
    if (!sig.accountId) return null;
    return accounts[sig.accountId.replace(/-/g, '')] || null;
  };

  const accountsWithSignals = [...new Set(signals.map(s => s.accountId).filter(Boolean))]
    .map(id => ({ id, name: accounts[id.replace(/-/g, '')] || id }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const filtered = signals
    .filter(sig => {
      if (derivedFilter === 'verified' && sig.isDerived)  return false;
      if (derivedFilter === 'derived'  && !sig.isDerived) return false;
      if (typeFilter !== 'all'      && sig.signalType     !== typeFilter)      return false;
      if (strengthFilter !== 'all'  && sig.signalStrength !== strengthFilter)  return false;
      if (freshnessFilter !== 'all' && sig.freshness      !== freshnessFilter) return false;
      if (accountFilter !== 'all'   && sig.accountId      !== accountFilter)   return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(sig.name || '').toLowerCase().includes(q) &&
            !(sig.aiInterpretation || '').toLowerCase().includes(q) &&
            !(getAccountName(sig) || '').toLowerCase().includes(q)) return false;
      }
      return true;
    })
    .sort((a, b) => signalScore(b) - signalScore(a));

  const verifiedCount = signals.filter(s => !s.isDerived).length;
  const derivedCount  = signals.filter(s => s.isDerived).length;
  const strongCount   = signals.filter(s => !s.isDerived && s.signalStrength === 'Strong').length;
  const freshCount    = signals.filter(s => s.freshness === 'fresh').length;

  return (
    <div className="sp-page">
      <div className="sp-header">
        <div className="sp-header-left">
          <h1 className="sp-title">📡 Strategic Signals</h1>
          <p className="sp-subtitle">Verified intelligence and derived insights across your account portfolio</p>
        </div>
        {/* P0.31 31.B.5 — the Archived view. Every entity that soft-deletes now has
            one, so a user can find what they hid instead of it being gone in
            practice while the row is still there. */}
        <button
          className={`sp-refresh-btn${showArchived ? ' sp-archived-on' : ''}`}
          onClick={() => setShowArchived(v => !v)}
          disabled={loading}
          title={showArchived ? 'Back to active signals' : 'Show archived signals'}
        >
          {showArchived ? '← Active' : 'Archived'}
        </button>
        <button className="sp-refresh-btn" onClick={fetchData} disabled={loading}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {!loading && !error && (
        <div className="sp-stats">
          <div className="sp-stat">
            <span className="sp-stat-num">{signals.length}</span>
            <span className="sp-stat-label">Total</span>
          </div>
          <div className="sp-stat">
            <span className="sp-stat-num">{verifiedCount}</span>
            <span className="sp-stat-label">✅ Verified</span>
          </div>
          <div className="sp-stat sp-stat--derived">
            <span className="sp-stat-num">{derivedCount}</span>
            <span className="sp-stat-label">🧠 Derived</span>
          </div>
          <div className="sp-stat sp-stat--strong">
            <span className="sp-stat-num">{strongCount}</span>
            <span className="sp-stat-label">🔥 Strong</span>
          </div>
          <div className="sp-stat sp-stat--fresh">
            <span className="sp-stat-num">{freshCount}</span>
            <span className="sp-stat-label">Fresh (≤30d)</span>
          </div>
        </div>
      )}

      <div className="sp-filters">
        <input className="sp-search" type="text" placeholder="Search signals, accounts…"
          value={search} onChange={e => setSearch(e.target.value)} />

        {/* Verified / Derived toggle */}
        <div className="sp-filter-group">
          <label className="sp-filter-label">Intelligence Type</label>
          <div className="sp-filter-pills">
            {[
              { key: 'all',      label: `All (${signals.length})` },
              { key: 'verified', label: `✅ Verified (${verifiedCount})` },
              { key: 'derived',  label: `🧠 Derived (${derivedCount})` },
            ].map(({ key, label }) => (
              <button key={key} className={`sp-pill ${derivedFilter === key ? 'sp-pill--active' : ''}`}
                onClick={() => setDerivedFilter(key)}>{label}</button>
            ))}
          </div>
        </div>

        <div className="sp-filter-group">
          <label className="sp-filter-label">Strength</label>
          <div className="sp-filter-pills">
            {['all', 'Strong', 'Moderate', 'Weak'].map(s => (
              <button key={s} className={`sp-pill ${strengthFilter === s ? 'sp-pill--active' : ''}`}
                onClick={() => setStrengthFilter(s)}>
                {s === 'all' ? 'All' : `${STRENGTH_CONFIG[s]?.icon} ${s}`}
              </button>
            ))}
          </div>
        </div>

        <div className="sp-filter-group">
          <label className="sp-filter-label">Freshness</label>
          <div className="sp-filter-pills">
            {['all', 'fresh', 'recent', 'aging', 'stale'].map(f => (
              <button key={f} className={`sp-pill ${freshnessFilter === f ? 'sp-pill--active' : ''}`}
                onClick={() => setFreshnessFilter(f)}>
                {f === 'all' ? 'All' : FRESHNESS_CONFIG[f]?.label}
              </button>
            ))}
          </div>
        </div>

        {accountsWithSignals.length > 0 && (
          <div className="sp-filter-group">
            <label className="sp-filter-label">Account</label>
            <select className="sp-account-select" value={accountFilter}
              onChange={e => setAccountFilter(e.target.value)}>
              <option value="all">All accounts</option>
              {accountsWithSignals.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!loading && !error && (
        <div className="sp-results-count">
          Showing {filtered.length} of {signals.length} signals
          {(derivedFilter !== 'all' || strengthFilter !== 'all' || freshnessFilter !== 'all' || accountFilter !== 'all' || search) && (
            <button className="sp-clear-filters"
              onClick={() => { setDerivedFilter('all'); setTypeFilter('all'); setStrengthFilter('all'); setFreshnessFilter('all'); setAccountFilter('all'); setSearch(''); }}>
              Clear filters
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="sp-loading"><div className="sp-loading-spinner" /><p>Loading signals…</p></div>
      ) : error ? (
        <div className="sp-error"><span>⚠️ {error}</span><button onClick={fetchData}>Retry</button></div>
      ) : filtered.length === 0 ? (
        <div className="sp-empty">
          <div className="sp-empty-icon">📡</div>
          <p className="sp-empty-title">No signals match your filters</p>
          <p className="sp-empty-sub">Try adjusting the filters above or clear them to see all signals.</p>
        </div>
      ) : (
        <div className="sp-grid">
          {filtered.map(sig => (
            <SignalCard key={sig.id} signal={sig} accountName={getAccountName(sig)} onNavigate={onNavigate}
              onArchive={archiveSignal} onRestore={restoreSignal} busy={!!acting[sig.id]} />
          ))}
        </div>
      )}
    </div>
  );
}
